import * as React from "react";
import Flex from "antd/es/flex";
import Splitter from "antd/es/splitter";
import Select from "antd/es/select";
import Form from "antd/es/form";
import Button from "antd/es/button";
import Input from "antd/es/input";
import Space from "antd/es/space";
import Spin from "antd/es/spin";
import useBreakpoint from "antd/lib/grid/hooks/useBreakpoint";
import RightOutlined from "@ant-design/icons/RightOutlined";
import LeftOutlined from "@ant-design/icons/LeftOutlined";
import InputNumber from "antd/es/input-number";
import { toTokens } from "thirdweb";
import message from "antd/es/message";
import { Chains, FormModel, ReservesType } from "./types";
import { decimals, fields, options, reservesValidator, walletsValidator } from "./constants";
import { useRates } from "../api";
import { ChainConnectButton } from "./ChainConnectButton";
import { displayReserve, validateReserveAmount } from "./utils";
import { TransferLogo } from "./TransferLogo";

export interface TransferFormProps {
    onFinish: (values: FormModel) => (Promise<void> | void);
    reserves: ReservesType;
    initialValues: FormModel;
    onBack: () => void;
}

export const TransferForm: React.FunctionComponent<TransferFormProps> = (props) => {
    const [api, contextHandler] = message.useMessage();
    const screenMap = useBreakpoint();
    const [form] = Form.useForm<FormModel>();
    const toChain: Chains | undefined = Form.useWatch(fields.toChain, form);
    const fromAmount: string | undefined = Form.useWatch(fields.fromAmount, form);
    const rate = useRates();
    const currentRequestId = React.useRef(0);
    const [loading, setLoading] = React.useState(false);

    React.useEffect(() => {
        (async () => {
            currentRequestId.current++;
            if (fromAmount && toChain) {
                if (validateReserveAmount(props.reserves, toChain, fromAmount, props.initialValues.token)) {
                    try {
                        const { amount, requestId } = await rate.mutateAsync({
                            fromAmount: fromAmount,
                            toChain,
                            requestId: currentRequestId.current,
                            token: props.initialValues.token,
                        });
                        if (requestId === currentRequestId.current) {
                            form.setFieldValue(fields.toAmount, toTokens(amount, decimals[toChain][props.initialValues.token]));
                        }
                    } catch (e) {
                        console.error(e);
                        form.setFields([{
                            name: fields.toAmount,
                            value: "",
                            errors: [`Exchange failed to load data, try again later or lower the transferred amount (max: ${displayReserve(
                                props.reserves,
                                toChain,
                                props.initialValues.token,
                            )})`],
                        }]);
                    }
                } else {
                    form.setFields([{
                        name: fields.toAmount,
                        value: "",
                        errors: [`Lower the transferred amount (max: ${displayReserve(
                            props.reserves,
                            toChain,
                            props.initialValues.token,
                        )})`],
                    }]);
                }
            }
        })()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fromAmount, toChain]);

    React.useEffect(() => {
        if (toChain) {
            form.setFieldValue(fields.recipient, "");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [toChain]);

    return (
        <Form
            form={form}
            layout="vertical"
            onFinish={async (values) => {
                try {
                    setLoading(true);
                    if (rate.isSuccess) {
                        await props.onFinish(values);
                    } else {
                        api.error("Please wait until we calculate received amount")
                    }
                } catch (e) {
                    console.error(e);
                    api.error("Failed to create order");
                } finally {
                    setLoading(false);
                }
            }}
            initialValues={props.initialValues}
            disabled={loading}
        >
            {contextHandler}
            <Form.Item name={fields.token} hidden>
                <Input />
            </Form.Item>
            <Flex vertical justify="center" align="center" gap={20}>
                <Splitter className="Exchange" layout={screenMap.md ? "horizontal" : "vertical"}>
                    <Splitter.Panel className="Exchange__panel" resizable={false} size={screenMap.md ? undefined : "250px"}>
                        <Flex justify="center" align="center" vertical gap="15px">
                            <div className="Exchange__container">
                                <Form.Item
                                    name={fields.fromChain}
                                    label="Select source chain"
                                    className="Exchange__field"
                                    rules={[{ required: true }]}
                                    required
                                >
                                    <Select
                                        size="large"
                                        options={options}
                                        className="Exchange__select"
                                        onSelect={(value?: Chains) => {
                                            if (value && value === form.getFieldValue(fields.toChain)) {
                                                form.setFieldValue(fields.toChain, undefined);
                                            }
                                        }}
                                    />
                                </Form.Item>
                                <Form.Item
                                    name={fields.fromAmount}
                                    label="Transfer amount"
                                    className="Exchange__field Exchange__field--icon"
                                    rules={[{ required: true }]}
                                    required
                                >
                                    <InputNumber
                                        className="Exchange__numeric"
                                        controls={false}
                                        addonAfter={<TransferLogo token={props.initialValues.token} />}
                                        stringMode
                                        size="large"
                                    />
                                </Form.Item>
                            </div>

                        </Flex>
                    </Splitter.Panel>
                    <Splitter.Panel className="Exchange__panel" resizable={false} size={screenMap.md ? undefined : "250px"}>
                        <Flex justify="center" align="center" vertical gap="15px">
                            <div className="Exchange__container">
                                <Form.Item
                                    name={fields.toChain}
                                    label="Select destination chain"
                                    className="Exchange__field"
                                    rules={[{ required: true }]}
                                    required
                                >
                                    <Select
                                        size="large"
                                        options={options}
                                        className="Exchange__select"
                                        onSelect={(value?: Chains) => {
                                            if (value && value === form.getFieldValue(fields.fromChain)) {
                                                form.setFieldValue(fields.fromChain, undefined);
                                            }
                                        }}
                                        allowClear
                                    />
                                </Form.Item>
                                <Form.Item
                                    label="Recipient"
                                    extra="Wallet to receive exchanged funds"
                                    required
                                    className="Exchange__field Exchange__field--icon"
                                    hidden={!toChain}
                                >
                                    <Space.Compact size="large" className="Exchange__space">
                                        <Form.Item
                                            name={fields.recipient}
                                            rules={[
                                                { required: true },
                                                {
                                                    validator: async (_, value: string) => {
                                                        if (toChain && value) {
                                                            walletsValidator[toChain](value);
                                                        }
                                                    }
                                                }
                                            ]}
                                            noStyle
                                        >
                                            <Input
                                                placeholder="Wallet address"
                                                size="large"
                                            />
                                        </Form.Item>
                                        {toChain && (
                                            <ChainConnectButton
                                                chain={toChain}
                                                selectWallet={(wallet) => {
                                                    if (form.getFieldValue(fields.recipient) !== wallet) {
                                                        form.setFieldValue(fields.recipient, wallet);
                                                    }
                                                }}
                                                token={props.initialValues.token}
                                            />
                                        )}
                                    </Space.Compact>
                                </Form.Item>
                                <Form.Item
                                    name={fields.toAmount}
                                    label="Receive amount"
                                    className="Exchange__field Exchange__field--icon"
                                    extra="Displays amount to be received"
                                    rules={[
                                        { required: true, message: "Wait for API to calculate exchange" },
                                        {
                                            validator: async (_, value: string) => {
                                                if (parseFloat(value) <= 0) {
                                                    throw new Error("Transfered amount must be greater than 0");
                                                }
                                                if (value && toChain) {
                                                    reservesValidator[toChain](value, props.initialValues.token, props.reserves);
                                                }
                                            }
                                        }
                                    ]}
                                    hidden={Boolean(!toChain || !fromAmount)}
                                >
                                    <InputNumber
                                        className="Exchange__numeric"
                                        controls={false}
                                        readOnly
                                        addonAfter={rate.isPending ? (
                                            <Spin />
                                        ) : <TransferLogo token={props.initialValues.token} />}
                                        stringMode
                                        size="large"
                                    />
                                </Form.Item>
                            </div>
                        </Flex>
                    </Splitter.Panel>
                </Splitter>
                <Flex wrap gap="15px">
                    <Button
                        onClick={props.onBack}
                        htmlType="button"
                        icon={<LeftOutlined />}
                        iconPosition="start"
                        className="BackButton BackButton--small"
                    >
                        Back
                    </Button>
                    <Button
                        type="primary"
                        color="primary"
                        htmlType="submit"
                        icon={loading ? <Spin /> : <RightOutlined />}
                        iconPosition="end"
                        size="large"
                        disabled={rate.isPending || loading}
                        className="CreateOrderButton"
                    >
                        Create Order
                    </Button>
                </Flex>
            </Flex>
        </Form>
    );
};